import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { systemDecimalFormat } from '~Betslip/utils/numberFormatters';
import {
    deLocalizeNumberString,
} from '~shared/utils/currencyUtils';
import { useBetslipKeypadAnimationEnabled } from '~SpectateStore/hooks/clientStateHooks';

const ANIMATION_DURATION = 130; // milliseconds

const KeyboardContext = React.createContext();

const useKeyboard = () => {
    const context = useContext(KeyboardContext);

    return context;
};

const KeyboardProvider = ({children}) => {
    const [ keyboardContainerRef, setInternalKeyboardContainerRef ] = useState(null);
    const [ stakeFieldRef, setStakeFieldRef ] = useState(null);
    const [ freebets, setFreebets ] = useState([]);
    const [ selectedFreebet, setSelectedFreebet ] = useState(null);
    const [ isKeyboardVisible, setIsKeyboardVisible ] = useState(false);
    const isAnimationEnabled = useBetslipKeypadAnimationEnabled();
    const animationDelay = isAnimationEnabled ? ANIMATION_DURATION : 0;

    useEffect(() => {
        if (keyboardContainerRef) {
            setIsKeyboardVisible(true);
        }
    }, [keyboardContainerRef]);

    const setKeyboardContainerRef = (ref) => {
        if (ref === keyboardContainerRef && isKeyboardVisible) {
            // Do nothing if the input is already focused and the keyboard is visible
            return;
        }

        if (ref) {
            if (keyboardContainerRef !== ref && isKeyboardVisible) {
                // Hide the keyboard first if moving focus to a different input
                setIsKeyboardVisible(false);
                setTimeout(() => {
                    // Then update the ref and show the keyboard for the new input after the delay
                    setInternalKeyboardContainerRef(ref);
                    setIsKeyboardVisible(true);
                }, animationDelay);
            } else {
                // If there's no previous focus or the keyboard is not visible, show it directly
                setInternalKeyboardContainerRef(ref);
                setIsKeyboardVisible(true);
            }
        } else {
            // Hide the keyboard when ref is null
            setIsKeyboardVisible(false);
            setTimeout(() => {
                // Clear the internal reference after the hide animation completes
                setInternalKeyboardContainerRef(ref);
            }, animationDelay);
        }
    };

    const triggerInputOnChange = (newValue, withFocus = true, freebet = null) => {
        if (!stakeFieldRef.current) {
            return;
        }

        const input = stakeFieldRef.current;
        input.freebetId = freebet?.id || null;
        const currentValue = input.value;
        input.value = newValue;
        input._valueTracker.setValue(currentValue);
        input.dispatchEvent(
            new Event('input', { bubbles: true })
        );

        if (withFocus) {
            input.focus();
        }
    };

    const addChar = (char) => {
        const currentValue = stakeFieldRef.current.value;
        const newValue = `${currentValue}${char}`;

        triggerInputOnChange(newValue);
    };

    const removeLastChar = () => {
        if (selectedFreebet) {
            setSelectedFreebet(null);
            triggerInputOnChange('');
        }
        triggerInputOnChange(
            stakeFieldRef.current.value.slice(0, -1)
        );
    };

    const incrementBy = (value) => {
        if (selectedFreebet) {
            setSelectedFreebet(null);
            triggerInputOnChange('');
        }
        const currentValue = deLocalizeNumberString(stakeFieldRef.current.value);
        const incrementedValue = Number(currentValue) + Number(value);
        triggerInputOnChange(
            systemDecimalFormat(incrementedValue)
        );
    };

    const closeKeyboard = () => {
        setIsKeyboardVisible(false);
        setTimeout(() => {
            setKeyboardContainerRef(null);
            setStakeFieldRef(null);
        }, animationDelay);
    };

    const selectFreebet = (freebet) => {
        triggerInputOnChange('', true, freebet);
        setSelectedFreebet(freebet);
    };

    const value = {
        // Keyboard Ref
        keyboardContainerRef,
        setKeyboardContainerRef,

        // Keyboard visibility
        isKeyboardVisible,

        // Stake Field Ref
        stakeFieldRef,
        setStakeFieldRef,

        // API
        closeKeyboard,
        addChar,
        removeLastChar,
        incrementBy,

        // Freebets
        freebets,
        setFreebets,
        selectedFreebet,
        selectFreebet,
        setSelectedFreebet,
    };

    return <KeyboardContext.Provider value={value}>
        {children}
    </KeyboardContext.Provider>;
};

KeyboardProvider.propTypes = {
    children: PropTypes.element.isRequired,
};

export {
    KeyboardProvider,
    useKeyboard,
    ANIMATION_DURATION,
};
